import type { Dayjs } from '@utils/dayjs';
import { DATE_FORMATS } from '@callmart/types';

// Minimum WorkingHour records (day + timeSlot combinations) to consider working hours as "configured"
// Each record represents one 30-minute slot for a specific day of week
// A typical work week has ~100-160 slots (5 days × 10-16 working hours × 2 slots/hour)
// With less than 5 records, assume working hours are not properly set up
export const WORKING_HOURS_MIN_THRESHOLD = 5;

export interface WorkingHour {
    dayTimeSlot: {
        day: number;
        timeSlot: string;
    };
    isWorking: boolean;
}

export interface DayCellData {
    isWorking?: boolean;
    total?: number;
    value?: number;
    percentage?: number;
}

/**
 * Check if working hours are properly configured
 * Returns true only if we have at least WORKING_HOURS_MIN_THRESHOLD records
 */
export const hasConfiguredWorkingHours = (workingHours: WorkingHour[] | undefined): boolean => {
    return (workingHours?.length ?? 0) >= WORKING_HOURS_MIN_THRESHOLD;
};

/**
 * Check if a cell should be considered as "working" for filtering purposes
 * @param cell - The cell data containing isWorking flag and values
 * @param hasWorkingHoursConfig - Whether working hours are configured for this unit
 */
export const isCellWorking = (cell: DayCellData | undefined, hasWorkingHoursConfig: boolean): boolean => {
    if (!cell) return false;

    // If no working hours configured, consider all cells with data as "working"
    if (!hasWorkingHoursConfig) {
        return cell.total != null || cell.value != null || cell.percentage != null;
    }

    // With working hours configured, only isWorking: true is working
    return cell.isWorking === true;
};

/**
 * Check if a cell has meaningful data (for keeping rows with data even if non-working)
 * This ensures rows with actual data are not hidden even if they're in non-working hours
 */
export const cellHasData = (cell: DayCellData | undefined): boolean => {
    if (!cell) return false;
    const value = cell.total ?? cell.value ?? cell.percentage;
    return value != null && value > 0;
};

/**
 * Filter function for rows based on working hours
 * Keeps a row if ANY day is working OR has data > 0
 *
 * @param allDatesInAMonth - Array of dates to check in each row
 * @param workingHours - Working hours configuration
 * @param hideNonWorkingTime - Whether filtering is enabled
 * @returns A filter function that can be used with Array.filter()
 */
export const createWorkingHoursRowFilter = <T extends Record<string, unknown>>(
    allDatesInAMonth: Dayjs[],
    workingHours: WorkingHour[] | undefined,
    hideNonWorkingTime: boolean
): ((row: T) => boolean) => {
    const hasWorkingHoursConfig = hasConfiguredWorkingHours(workingHours);

    return (row: T): boolean => {
        // If filter disabled, keep all rows
        if (!hideNonWorkingTime) return true;

        // If no working hours configured, keep all rows
        if (!hasWorkingHoursConfig) return true;

        // Check if any day in the row is working or has data
        return allDatesInAMonth.some((date) => {
            const dayKey = `day${date.format(DATE_FORMATS.DAY_ONLY)}`;
            const dayData = row[dayKey] as DayCellData | undefined;

            return isCellWorking(dayData, hasWorkingHoursConfig) || cellHasData(dayData);
        });
    };
};
